// Availability — week-grid time-calendar (faithful port of the doctor-portal look) +
// recurring weekly hours (doctor_availability) and time-off / blocks
// (doctor_availability_exceptions, kind ∈ pto|block|extra). Operational scheduling;
// no record content. Reuses existing endpoints: availability(), setWeekly(),
// addException(), deleteException(), queue(), patients(), reschedule(), cancel().
//
// The calendar body uses an absolute-positioned event model at a FIXED 64px/hour
// (16px per 15-min step) so blocks can be drag-resized in 15-minute increments and
// appointment chips map cleanly to time. Blocks have no updateException endpoint, so
// a resize persists as add-new → delete-old (add first; only delete the old block once
// the new one is saved, so a failed add never loses the block).
import "../styles-availability.css";
import "../styles-schedule.css"; // appt-detail / reschedule / cancel modal styling
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Modal } from "@championcare/ui";
import { useApi } from "../api";
import { useAsync } from "../lib/useAsync";
import { weekdayLabel, formatTime, labelText } from "../lib/format";
import { Loading, ErrorState } from "../components/ui";
import type { QueueItem, RosterPatient } from "@forcebase-ai/schema/doctor";

interface WeekRow {
  weekday: number;
  on: boolean;
  starts_at: string;
  ends_at: string;
}

interface Exception {
  id: string;
  starts_at: string;
  ends_at: string;
  kind: string;
  reason?: string | null;
}

interface WeeklySlot {
  weekday: number;
  starts_at: string;
  ends_at: string;
}

// Calendar window: 8 AM → 5 PM (9 hours). hourIndex + DAY_START_HOUR = 24h clock.
const DAY_START_HOUR = 8;
const DAY_END_HOUR = 17;
const HOURS = DAY_END_HOUR - DAY_START_HOUR; // 9
const PX_PER_HOUR = 64;
const PX_PER_MIN = PX_PER_HOUR / 60; // ~1.0667
const STEP_MIN = 15;
const BODY_HEIGHT = HOURS * PX_PER_HOUR;
const DAY_START_MIN = DAY_START_HOUR * 60;
const DAY_END_MIN = DAY_END_HOUR * 60;

const DAY_NAMES_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAY_NAMES_FULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const DEFAULT_CANCEL_REASON = "Patient requested cancellation";
const CANCEL_REASONS: string[] = [
  DEFAULT_CANCEL_REASON,
  "Provider unavailable",
  "Rescheduling to different time",
  "Patient no-show",
  "Patient illness",
  "Other",
];

function buildRows(weekly: WeeklySlot[]): WeekRow[] {
  return Array.from({ length: 7 }, (_, wd) => {
    const found = weekly.find((w) => w.weekday === wd);
    return {
      weekday: wd,
      on: Boolean(found),
      starts_at: (found?.starts_at ?? "09:00").slice(0, 5),
      ends_at: (found?.ends_at ?? "17:00").slice(0, 5),
    };
  });
}

function mondayOfWeek(offset: number): Date {
  const t = new Date();
  t.setHours(0, 0, 0, 0);
  const day = t.getDay();
  const shiftToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(t);
  monday.setDate(t.getDate() + shiftToMonday + offset * 7);
  return monday;
}

function weekDates(offset: number): Date[] {
  const start = mondayOfWeek(offset);
  return [0, 1, 2, 3, 4, 5, 6].map((i) => {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    return d;
  });
}

function sameDate(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

/** minutes-of-day from a Date. */
function minutesOfDay(d: Date): number {
  return d.getHours() * 60 + d.getMinutes();
}

/** clamp a minutes-of-day value into the visible window and snap to STEP_MIN. */
function snapMin(min: number): number {
  return Math.round(min / STEP_MIN) * STEP_MIN;
}
function clampMin(min: number): number {
  return Math.max(DAY_START_MIN, Math.min(DAY_END_MIN, min));
}

/** y-pixels within the day column → minutes-of-day (snapped). */
function pxToMin(px: number): number {
  return clampMin(snapMin(DAY_START_MIN + px / PX_PER_MIN));
}
/** minutes-of-day → top px within the column. */
function minToPx(min: number): number {
  return (min - DAY_START_MIN) * PX_PER_MIN;
}

/** "9:00 AM" style label for minutes-of-day. */
function clock(totalMin: number): string {
  const h24 = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12}:${String(m).padStart(2, "0")} ${period}`;
}
/** short "9 AM" style label for the hour gutter. */
function hourLabel(h24: number): string {
  const period = h24 >= 12 ? "PM" : "AM";
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24;
  return `${h12} ${period}`;
}

function durLabel(mins: number): string {
  if (mins % 60 === 0) return `${mins / 60} hr`;
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)} hr ${mins % 60} min`;
}

function initials(p?: RosterPatient): string {
  if (!p) return "··";
  return `${p.first_name.charAt(0)}${p.last_name.charAt(0)}`.toUpperCase();
}

// --- Icons -----------------------------------------------------------------
const ChevronLeft = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);
const ChevronRight = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);
const PlusIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function Availability() {
  const api = useApi();
  const { data, loading, error, reload } = useAsync(() => api.availability(), []);
  const queue = useAsync(() => api.queue(), []);
  const patients = useAsync(() => api.patients(), []);

  const [offset, setOffset] = useState(0);
  const [showWeekly, setShowWeekly] = useState(false);
  const [showPto, setShowPto] = useState(false);
  const [showAddBlock, setShowAddBlock] = useState(false);
  const [addBlockDay, setAddBlockDay] = useState<Date | null>(null);
  const [selectedAppt, setSelectedAppt] = useState<QueueItem | null>(null);

  const weekly = data?.weekly ?? [];
  const exceptions: Exception[] = data?.exceptions ?? [];
  const appts = queue.data ?? [];
  const dates = useMemo(() => weekDates(offset), [offset]);

  const roster = useMemo(
    () => new Map((patients.data ?? []).map((p) => [p.id, p])),
    [patients.data],
  );
  const nameOf = (id: string) => {
    const p = roster.get(id);
    return p ? `${p.first_name} ${p.last_name}` : "Patient";
  };

  // Only block on the FIRST load (no data yet). Once the grid is mounted, a reload()
  // refreshes data in place — never unmount the scroll container, or scroll resets.
  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorState message={error} />;

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const first = dates[0]!;
  const last = dates[dates.length - 1]!;
  const sameMonth = first.getMonth() === last.getMonth();
  const sameYear = first.getFullYear() === last.getFullYear();
  const startStr = `${MONTH_SHORT[first.getMonth()]} ${first.getDate()}`;
  const endStr = sameMonth ? `${last.getDate()}` : `${MONTH_SHORT[last.getMonth()]} ${last.getDate()}`;
  const yearSuffix = sameYear ? "" : ` ${last.getFullYear()}`;
  let prefix: string;
  if (offset === 0) prefix = "This week";
  else if (offset === 1) prefix = "Next week";
  else if (offset === -1) prefix = "Last week";
  else if (offset > 1) prefix = `In ${offset} weeks`;
  else prefix = `${Math.abs(offset)} weeks ago`;

  // Recurring open window for a weekday → [startMin, endMin] within the visible day, or null.
  function openWindow(date: Date): [number, number] | null {
    const slot = weekly.find((w) => w.weekday === date.getDay());
    if (!slot) return null;
    const s = parseInt(slot.starts_at.slice(0, 2), 10) * 60 + parseInt(slot.starts_at.slice(3, 5), 10);
    const e = parseInt(slot.ends_at.slice(0, 2), 10) * 60 + parseInt(slot.ends_at.slice(3, 5), 10);
    return [Math.max(s, DAY_START_MIN), Math.min(e, DAY_END_MIN)];
  }

  // Exceptions (block/pto, not "extra") that intersect a given calendar day.
  function blocksForDay(date: Date): Exception[] {
    return exceptions.filter((x) => {
      if (x.kind === "extra") return false;
      const xs = new Date(x.starts_at);
      const xe = new Date(x.ends_at);
      return xs < endOfDay(date) && xe > startOfDayD(date);
    });
  }
  function apptsForDay(date: Date): QueueItem[] {
    return appts.filter((a) => sameDate(new Date(a.scheduled_at), date));
  }

  // Add a 1-hour block at a clicked open slot (matches prior default; then drag to resize).
  async function quickAddBlock(date: Date, startMin: number) {
    const s = new Date(date);
    s.setHours(0, startMin, 0, 0);
    const e = new Date(s.getTime() + 60 * 60 * 1000);
    await api.addException({ starts_at: s.toISOString(), ends_at: e.toISOString(), kind: "block" });
    reload();
  }

  async function removeBlock(ex: Exception) {
    await api.deleteException(ex.id);
    reload();
  }

  // Persist a resize: add the new range, then delete the old (guarded so a failed add
  // never loses the block). starts/ends are minutes-of-day on the block's date.
  async function persistResize(ex: Exception, date: Date, startMin: number, endMin: number) {
    const s = new Date(date);
    s.setHours(0, startMin, 0, 0);
    const e = new Date(date);
    e.setHours(0, endMin, 0, 0);
    const res = await api.addException({
      starts_at: s.toISOString(),
      ends_at: e.toISOString(),
      kind: ex.kind === "pto" ? "pto" : "block",
      reason: ex.reason ?? undefined,
    });
    if (res?.id || res) {
      await api.deleteException(ex.id);
    }
    reload();
  }

  return (
    <>
      <div className="dp-head">
        <div>
          <div className="dp-head__greet">Calendar &amp; hours</div>
          <h1 className="dp-head__title">
            Your <em>availability.</em>
          </h1>
        </div>
      </div>

      {/* ===== WEEK TIME-GRID CALENDAR ===== */}
      <div className="calendar-wrap">
        <div className="calendar-head">
          <div>
            <h3 dangerouslySetInnerHTML={{ __html: `${prefix}, <em>${startStr}–${endStr}${yearSuffix}</em>` }} />
            <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>
              Click an open slot to block it, drag a block&rsquo;s edges to resize, or add one manually.
            </div>
          </div>
          <div className="block-head-actions">
            <button
              className="dp-btn dp-btn--primary"
              onClick={() => {
                setAddBlockDay(dates.find((d) => sameDate(d, today)) ?? dates[0]!);
                setShowAddBlock(true);
              }}
            >
              {PlusIcon} Block time
            </button>
            <div className="calendar-nav">
              <button className="btn-icon" aria-label="Previous week" onClick={() => setOffset((o) => o - 1)}>
                {ChevronLeft}
              </button>
              <button className="dp-btn dp-btn--ghost" disabled={offset === 0} onClick={() => setOffset(0)}>
                Today
              </button>
              <button className="btn-icon" aria-label="Next week" onClick={() => setOffset((o) => o + 1)}>
                {ChevronRight}
              </button>
            </div>
          </div>
        </div>

        <div className="cal-scroll">
          <div className="cal-grid-head">
            <div className="cal-corner" />
            {dates.map((d) => {
              const isToday = sameDate(d, today);
              return (
                <div key={d.toISOString()} className={`cal-header-cell ${isToday ? "today" : ""}`}>
                  <div className="day-name">{DAY_NAMES_SHORT[d.getDay()]}</div>
                  <div className="day-num">{d.getDate()}</div>
                </div>
              );
            })}
          </div>

          <div className="cal-body" style={{ height: BODY_HEIGHT }}>
            <div className="cal-gutter">
              {Array.from({ length: HOURS + 1 }, (_, i) => (
                <div key={i} className="cal-hour-label" style={{ top: i * PX_PER_HOUR }}>
                  {i < HOURS ? hourLabel(DAY_START_HOUR + i) : ""}
                </div>
              ))}
            </div>

            {dates.map((d) => (
              <DayColumn
                key={d.toISOString()}
                date={d}
                isToday={sameDate(d, today)}
                openWin={openWindow(d)}
                blocks={blocksForDay(d)}
                dayAppts={apptsForDay(d)}
                nameOf={nameOf}
                onAddAt={quickAddBlock}
                onRemove={removeBlock}
                onResize={persistResize}
                onApptClick={setSelectedAppt}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ===== QUICK ACTIONS ===== */}
      <div style={{ marginTop: 32 }}>
        <div className="availability-options">
          <button
            className="avail-card"
            onClick={() => {
              setAddBlockDay(dates.find((d) => sameDate(d, today)) ?? dates[0]!);
              setShowAddBlock(true);
            }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <div className="title">Block off time</div>
            <div className="desc">Mark a range as unavailable for new bookings.</div>
          </button>
          <button className="avail-card" onClick={() => setShowWeekly(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <div className="title">Set weekly hours</div>
            <div className="desc">Define your standard recurring availability for the week.</div>
          </button>
          <button className="avail-card" onClick={() => setShowPto(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round">
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
            </svg>
            <div className="title">Request time off</div>
            <div className="desc">Submit vacation or PTO that auto-blocks your calendar.</div>
          </button>
        </div>
      </div>

      {showWeekly && <WeeklyEditor weekly={weekly} onClose={() => setShowWeekly(false)} onSaved={reload} />}
      {showPto && <TimeOff exceptions={exceptions} onClose={() => setShowPto(false)} onChanged={reload} />}
      {showAddBlock && (
        <AddBlockModal
          defaultDay={addBlockDay ?? today}
          onClose={() => setShowAddBlock(false)}
          onAdded={reload}
        />
      )}
      <ApptDetailModal
        appt={selectedAppt}
        nameOf={nameOf}
        roster={roster}
        onClose={() => setSelectedAppt(null)}
        onChanged={() => queue.reload()}
      />
    </>
  );
}

function startOfDayD(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}
function endOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(23, 59, 59, 999);
  return n;
}

// ===========================================================================
// Day column — hosts open/off slots, appointment chips, and resizable blocks.
// ===========================================================================
function DayColumn({
  date,
  isToday,
  openWin,
  blocks,
  dayAppts,
  nameOf,
  onAddAt,
  onRemove,
  onResize,
  onApptClick,
}: {
  date: Date;
  isToday: boolean;
  openWin: [number, number] | null;
  blocks: Exception[];
  dayAppts: QueueItem[];
  nameOf: (id: string) => string;
  onAddAt: (date: Date, startMin: number) => void;
  onRemove: (ex: Exception) => void;
  onResize: (ex: Exception, date: Date, startMin: number, endMin: number) => void;
  onApptClick: (a: QueueItem) => void;
}) {
  // hour slots — background. Open ones are clickable (when not covered by a block).
  const slots = Array.from({ length: HOURS }, (_, i) => {
    const slotStartMin = DAY_START_MIN + i * 60;
    const isOpen = openWin ? slotStartMin >= openWin[0] && slotStartMin < openWin[1] : false;
    return { i, slotStartMin, isOpen };
  });

  // Current minutes-of-day — used to disable already-passed slots on today's column (a block whose
  // hour has fully passed can't be created: the API hides exceptions that already ended).
  const nowMin = (() => {
    const n = new Date();
    return n.getHours() * 60 + n.getMinutes();
  })();

  function blockAt(min: number): boolean {
    return blocks.some((b) => {
      const bs = clampVisible(new Date(b.starts_at));
      const be = clampVisible(new Date(b.ends_at));
      return min >= bs && min < be;
    });
  }

  return (
    <div className={`cal-col ${isToday ? "is-today" : ""}`}>
      {slots.map(({ i, slotStartMin, isOpen }) => {
        const covered = blockAt(slotStartMin);
        const isPast = isToday && slotStartMin + 60 <= nowMin;
        const clickable = isOpen && !covered && !isPast;
        const cls = ["cal-slot"];
        if (clickable) cls.push("open");
        else if (!isOpen || isPast) cls.push("off");
        return (
          <div
            key={i}
            className={cls.join(" ")}
            style={{ top: i * PX_PER_HOUR, height: PX_PER_HOUR }}
            onClick={clickable ? () => onAddAt(date, slotStartMin) : undefined}
            title={clickable ? "Open — click to block off" : isPast ? "Already passed" : isOpen ? undefined : "Outside recurring hours"}
            role={clickable ? "button" : undefined}
          />
        );
      })}

      {dayAppts.map((a) => {
        const start = minutesOfDay(new Date(a.scheduled_at));
        const dur = a.duration_minutes || 30;
        const topMin = clampVisible(new Date(a.scheduled_at));
        if (start >= DAY_END_MIN || start + dur <= DAY_START_MIN) return null;
        const endMin = clampMin(start + dur);
        const chipH = Math.max(20, minToPx(endMin) - minToPx(topMin));
        // Too short to stack time over name → lay them out on one horizontal row.
        const isShort = chipH < 40;
        return (
          <button
            key={a.id}
            type="button"
            className={`cal-event appt ${isShort ? "is-short" : ""}`}
            style={{ top: minToPx(topMin), height: chipH }}
            title={`${nameOf(a.patient_id)} · ${labelText(a.label)} — click for details`}
            onClick={() => onApptClick(a)}
          >
            <strong>{clock(start)}</strong>
            <span className="who">{nameOf(a.patient_id)}</span>
          </button>
        );
      })}

      {blocks.map((b) => (
        <BlockEvent key={b.id} ex={b} date={date} onRemove={onRemove} onResize={onResize} />
      ))}
    </div>
  );
}

/** Minutes-of-day of an instant, clamped into the visible window (handles multi-day blocks). */
function clampVisible(d: Date): number {
  // if the instant is before today's window the visible edge is the day-start; we only
  // call this for blocks already known to intersect the day.
  return clampMin(minutesOfDay(d));
}

// ===========================================================================
// Resizable block — drag top/bottom edges in 15-min steps, live label, persist on drop.
// ===========================================================================
function BlockEvent({
  ex,
  date,
  onRemove,
  onResize,
}: {
  ex: Exception;
  date: Date;
  onRemove: (ex: Exception) => void;
  onResize: (ex: Exception, date: Date, startMin: number, endMin: number) => void;
}) {
  const baseStart = clampVisible(new Date(ex.starts_at));
  const baseEnd = clampVisible(new Date(ex.ends_at));
  const [range, setRange] = useState<[number, number]>([baseStart, baseEnd]);
  const [dragging, setDragging] = useState<null | "top" | "bottom">(null);
  const [labelPos, setLabelPos] = useState<{ x: number; y: number } | null>(null);

  // reconcile when the underlying exception changes (after a reload)
  useEffect(() => {
    setRange([baseStart, baseEnd]);
  }, [baseStart, baseEnd]);

  const colRef = useRef<HTMLDivElement | null>(null);
  const rangeRef = useRef(range);
  rangeRef.current = range;

  function startDrag(edge: "top" | "bottom", e: React.PointerEvent) {
    e.preventDefault();
    e.stopPropagation();
    const col = (e.currentTarget as HTMLElement).closest(".cal-col") as HTMLElement | null;
    colRef.current = col as HTMLDivElement | null;
    setDragging(edge);

    function onMove(ev: PointerEvent) {
      const rect = colRef.current?.getBoundingClientRect();
      if (!rect) return;
      const y = ev.clientY - rect.top;
      const min = pxToMin(y);
      setRange((prev) => {
        let [s, en] = prev;
        if (edge === "top") s = Math.min(min, en - STEP_MIN);
        else en = Math.max(min, s + STEP_MIN);
        s = clampMin(s);
        en = clampMin(en);
        return [s, en];
      });
      setLabelPos({ x: ev.clientX + 14, y: ev.clientY - 10 });
    }
    function onUp() {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      setDragging(null);
      setLabelPos(null);
      const [s, en] = rangeRef.current;
      if (s !== baseStart || en !== baseEnd) {
        onResize(ex, date, s, en);
      }
    }
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
  }

  const [s, en] = range;
  const top = minToPx(s);
  const height = Math.max(PX_PER_MIN * STEP_MIN, minToPx(en) - minToPx(s));
  const isPto = ex.kind === "pto";
  const label = isPto ? "PTO" : "Blocked";

  return (
    <>
      <div
        className={`cal-block ${isPto ? "is-pto" : ""} ${dragging ? "dragging" : ""}`}
        style={{ top, height }}
      >
        <div
          className="cal-block__edge top"
          onPointerDown={(e) => startDrag("top", e)}
          aria-label="Resize block start"
          role="separator"
        />
        <div className="cal-block__body">
          <span className="cal-block__label">{label}</span>
          {height >= 36 && <span className="cal-block__time">{clock(s)}–{clock(en)}</span>}
          {ex.reason && height >= 52 && <span className="cal-block__reason">{ex.reason}</span>}
        </div>
        <button
          className="cal-block__rm"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(ex);
          }}
          aria-label="Remove block"
          title="Remove block"
        >
          ×
        </button>
        <div
          className="cal-block__edge bottom"
          onPointerDown={(e) => startDrag("bottom", e)}
          aria-label="Resize block end"
          role="separator"
        />
      </div>
      {dragging && labelPos && (
        <div className="cal-drag-label" style={{ left: labelPos.x, top: labelPos.y }}>
          {clock(s)}–{clock(en)} · {durLabel(en - s)}
        </div>
      )}
    </>
  );
}

// ===========================================================================
// Manual "Add blocked time" modal — date + start + duration chips, live summary.
// ===========================================================================
const BLOCK_NOTE_PRESETS = ["", "Lunch", "Admin", "Personal", "Break"];

function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function AddBlockModal({
  defaultDay,
  onClose,
  onAdded,
}: {
  defaultDay: Date;
  onClose: () => void;
  onAdded: () => void;
}) {
  const api = useApi();
  const [dateStr, setDateStr] = useState(toDateInput(defaultDay));
  const [startMin, setStartMin] = useState(12 * 60); // default noon
  const [endMin, setEndMin] = useState(13 * 60); // default 1 PM (1 hr)
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // When the selected day is today you can't block a time that has already passed — the API hides
  // exceptions whose end is in the past, so such a block would save but never appear. The earliest
  // selectable start is therefore the next 15-min slot from now.
  const minStart = useMemo(() => {
    if (dateStr !== toDateInput(new Date())) return DAY_START_MIN;
    const n = new Date();
    const nowMin = n.getHours() * 60 + n.getMinutes();
    return Math.min(Math.max(Math.ceil(nowMin / STEP_MIN) * STEP_MIN, DAY_START_MIN), DAY_END_MIN - STEP_MIN);
  }, [dateStr]);
  // 15-min start options from the earliest valid start through the day window (every slot but the last).
  const startOptions = useMemo(() => {
    const out: number[] = [];
    for (let m = minStart; m < DAY_END_MIN; m += STEP_MIN) out.push(m);
    return out;
  }, [minStart]);
  // Keep the chosen start valid when the day (and thus minStart) changes — e.g. opening the modal in
  // the afternoon bumps the default noon start up to the next available slot.
  useEffect(() => {
    if (startMin < minStart) {
      const ns = Math.min(minStart, DAY_END_MIN - STEP_MIN);
      setStartMin(ns);
      setEndMin(Math.min(ns + 60, DAY_END_MIN));
    }
  }, [minStart]);
  // End options are every 15-min slot after the chosen start, through end of day.
  const endOptions = useMemo(() => {
    const out: number[] = [];
    for (let m = startMin + STEP_MIN; m <= DAY_END_MIN; m += STEP_MIN) out.push(m);
    return out;
  }, [startMin]);

  const duration = endMin - startMin;
  const valid = endMin > startMin && endMin <= DAY_END_MIN;

  function summaryDate(): Date {
    const [y, mo, dd] = dateStr.split("-").map(Number);
    return new Date(y!, (mo ?? 1) - 1, dd ?? 1);
  }

  const sd = summaryDate();
  const summary = `${DAY_NAMES_SHORT[sd.getDay()]} ${MONTH_SHORT[sd.getMonth()]} ${sd.getDate()}, ${clock(startMin)}–${clock(endMin)} · ${durLabel(duration)}`;

  async function submit() {
    if (!valid) return;
    setBusy(true);
    setErr(null);
    const s = new Date(sd);
    s.setHours(0, startMin, 0, 0);
    const e = new Date(sd);
    e.setHours(0, endMin, 0, 0);
    try {
      await api.addException({
        starts_at: s.toISOString(),
        ends_at: e.toISOString(),
        kind: "block",
        reason: reason.trim() || undefined,
      });
      onAdded();
      onClose();
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : "Could not add block");
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} wide labelledBy="addBlockTitle">
      <div className="block-modal-title" id="addBlockTitle">
        Block <em>time</em>
      </div>
      <div className="block-modal-sub">Mark a range of your calendar as unavailable for new bookings.</div>

      {err && <ErrorState message={err} />}

      <div className="block-field">
        <label htmlFor="blockDate">Date</label>
        <input id="blockDate" type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
      </div>

      <div className="block-row">
        <div className="block-field">
          <label htmlFor="blockStart">Start time</label>
          <select
            id="blockStart"
            value={startMin}
            onChange={(e) => {
              const v = Number(e.target.value);
              setStartMin(v);
              if (endMin <= v) setEndMin(Math.min(v + STEP_MIN, DAY_END_MIN));
            }}
          >
            {startOptions.map((m) => (
              <option key={m} value={m}>
                {clock(m)}
              </option>
            ))}
          </select>
        </div>
        <div className="block-field">
          <label htmlFor="blockEnd">End time</label>
          <select id="blockEnd" value={endMin} onChange={(e) => setEndMin(Number(e.target.value))}>
            {endOptions.map((m) => (
              <option key={m} value={m}>
                {clock(m)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="block-field">
        <label htmlFor="blockReason">Note (optional)</label>
        <div className="chip-row" style={{ marginBottom: 10 }}>
          {BLOCK_NOTE_PRESETS.filter(Boolean).map((p) => (
            <button
              key={p}
              type="button"
              className={`dur-chip ${reason === p ? "selected" : ""}`}
              onClick={() => setReason((r) => (r === p ? "" : p))}
            >
              {p}
            </button>
          ))}
        </div>
        <input
          id="blockReason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Lunch, Admin, Personal"
          maxLength={40}
        />
      </div>

      <div className={`block-summary ${valid ? "" : "invalid"}`}>
        {valid ? (
          <>
            Blocking <strong>{summary}</strong>
          </>
        ) : (
          <>End time runs past {clock(DAY_END_MIN)} — shorten the duration or start earlier.</>
        )}
      </div>

      <div className="block-foot">
        <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={submit} disabled={!valid || busy}>
          {busy ? "Adding…" : "Block time"}
        </button>
      </div>
    </Modal>
  );
}

// ===========================================================================
// Appointment-detail pop-out — detail / reschedule / cancel (ported from Schedule,
// minus the page-level quick-availability cards). Real reschedule()/cancel().
// ===========================================================================
const SLOT_MINUTES = 30;
const RS_DAY_START = 8 * 60;
const RS_DAY_END = 16 * 60 + 30;
const LUNCH_HOUR = 12;

const JoinIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="23 7 16 12 23 17 23 7" /><rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
  </svg>
);
const CalendarIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const RescheduleIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" /><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);
const CloseXIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const BackIcon = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

function rsStartOfDay(d: Date): Date {
  const n = new Date(d);
  n.setHours(0, 0, 0, 0);
  return n;
}
function rsDateForOffset(offset: number): Date {
  const d = rsStartOfDay(new Date());
  d.setDate(d.getDate() + offset);
  return d;
}
function rsOffsetForDate(iso: string): number {
  const today = rsStartOfDay(new Date()).getTime();
  const d = rsStartOfDay(new Date(iso)).getTime();
  return Math.round((d - today) / 86400000);
}
function rsMinutesOfDay(iso: string): number {
  const d = new Date(iso);
  return d.getHours() * 60 + d.getMinutes();
}
function isWeekend(d: Date): boolean {
  return d.getDay() === 0 || d.getDay() === 6;
}

type ModalView = "detail" | "reschedule" | "cancel";

function ApptDetailModal({
  appt,
  nameOf,
  roster,
  onClose,
  onChanged,
}: {
  appt: QueueItem | null;
  nameOf: (id: string) => string;
  roster: Map<string, RosterPatient>;
  onClose: () => void;
  onChanged: () => void;
}) {
  const api = useApi();
  const [view, setView] = useState<ModalView>("detail");
  const [rsOffset, setRsOffset] = useState<number | null>(null);
  const [rsTime, setRsTime] = useState<number | null>(null);
  const [cancelReason, setCancelReason] = useState(DEFAULT_CANCEL_REASON);
  const [cancelNote, setCancelNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  // Other appointments (for reschedule conflict detection) — loaded lazily.
  const others = useAsync(() => api.queue(), []);
  const items = others.data ?? [];

  useEffect(() => {
    if (appt) {
      setView("detail");
      setRsOffset(null);
      setRsTime(null);
      setCancelReason(DEFAULT_CANCEL_REASON);
      setCancelNote("");
      setActionError(null);
      setBusy(false);
    }
  }, [appt]);

  if (!appt) return <Modal open={false} onClose={onClose}>{null}</Modal>;
  const selected = appt;

  function close() {
    setBusy(false);
    onClose();
  }
  function refreshAndClose() {
    onChanged();
    close();
  }

  function rescheduleDateOptions(): number[] {
    const out: number[] = [];
    let o = 0;
    while (out.length < 14 && o < 40) {
      if (!isWeekend(rsDateForOffset(o))) out.push(o);
      o++;
    }
    return out;
  }

  type Slot = { min: number; status: "available" | "disabled"; label?: string; current?: boolean };
  function timeSlotsForOffset(offsetDays: number): Slot[] {
    const slots: Slot[] = [];
    const apptOffset = rsOffsetForDate(selected.scheduled_at);
    const apptMin = rsMinutesOfDay(selected.scheduled_at);
    for (let total = RS_DAY_START; total <= RS_DAY_END; total += SLOT_MINUTES) {
      const h24 = Math.floor(total / 60);
      if (h24 === LUNCH_HOUR) {
        slots.push({ min: total, status: "disabled", label: "Lunch" });
        continue;
      }
      const conflict = items.find((x) => {
        if (x.id === selected.id) return false;
        if (rsOffsetForDate(x.scheduled_at) !== offsetDays) return false;
        const xStart = rsMinutesOfDay(x.scheduled_at);
        const xEnd = xStart + (x.duration_minutes || SLOT_MINUTES);
        return total < xEnd && total + SLOT_MINUTES > xStart;
      });
      if (conflict) {
        slots.push({ min: total, status: "disabled", label: initials(roster.get(conflict.patient_id)) });
        continue;
      }
      const current = apptOffset === offsetDays && apptMin === total;
      slots.push({ min: total, status: "available", current });
    }
    return slots;
  }

  async function confirmReschedule() {
    if (rsOffset === null || rsTime === null) return;
    setBusy(true);
    setActionError(null);
    const when = rsStartOfDay(rsDateForOffset(rsOffset));
    when.setMinutes(rsTime);
    try {
      await api.reschedule(selected.id, {
        new_scheduled_at: when.toISOString(),
        reason: "Provider rescheduled appointment",
      });
      refreshAndClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Could not reschedule");
      setBusy(false);
    }
  }

  async function confirmCancel() {
    setBusy(true);
    setActionError(null);
    const reason = cancelNote.trim() ? `${cancelReason} — ${cancelNote.trim()}` : cancelReason;
    try {
      await api.cancel(selected.id, { reason });
      refreshAndClose();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : "Could not cancel");
      setBusy(false);
    }
  }

  const rsOptions = rescheduleDateOptions();
  const effectiveRsOffset = rsOffset ?? rsOffsetForDate(selected.scheduled_at);
  const slots = timeSlotsForOffset(effectiveRsOffset);
  const canConfirmRs =
    rsTime != null &&
    (effectiveRsOffset !== rsOffsetForDate(selected.scheduled_at) || rsTime !== rsMinutesOfDay(selected.scheduled_at));

  const d0 = new Date(selected.scheduled_at);

  return (
    <Modal open onClose={close} wide labelledBy="apptDetailTitle">
      <div className="sched-modal-title" id="apptDetailTitle">
        {view === "reschedule" ? (
          <>Reschedule <em>appointment</em></>
        ) : view === "cancel" ? (
          <>Cancel <em>appointment</em></>
        ) : (
          <>Appointment <em>details</em></>
        )}
      </div>
      <div className="sched-modal-sub">
        {nameOf(selected.patient_id)} · {DAY_NAMES_FULL[d0.getDay()]}, {MONTH_SHORT[d0.getMonth()]} {d0.getDate()} at{" "}
        {formatTime(selected.scheduled_at)}
      </div>

      {actionError && <ErrorState message={actionError} />}

      {view === "detail" && (
        <>
          <div className="appt-detail-head">
            <div className="avatar lg">{initials(roster.get(selected.patient_id))}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3>
                <Link className="appt-name-link" to={`/patients/${selected.patient_id}`} onClick={close}>
                  {nameOf(selected.patient_id)}
                </Link>
              </h3>
              <div className="meta">{labelText(selected.label)}</div>
              <div className="tags">
                <span className="tag">{labelText(selected.label)}</span>
                <span className="tag">Video</span>
              </div>
            </div>
          </div>
          <div className="appt-info-grid">
            <div>
              <div className="label">Date</div>
              <div className="value">
                {DAY_NAMES_FULL[d0.getDay()]}, {MONTH_SHORT[d0.getMonth()]} {d0.getDate()}
              </div>
            </div>
            <div>
              <div className="label">Time</div>
              <div className="value">
                {formatTime(selected.scheduled_at)} · {selected.duration_minutes} min
              </div>
            </div>
            <div>
              <div className="label">Visit type</div>
              <div className="value">Video telehealth</div>
            </div>
            <div>
              <div className="label">Status</div>
              <div className="value" style={{ textTransform: "capitalize" }}>
                {selected.status.replace(/_/g, " ")}
              </div>
            </div>
            <div className="full">
              <div className="label">Visit label</div>
              <div className="value">{labelText(selected.label)}</div>
            </div>
          </div>
          <div className="appt-quick-actions">
            <Link className="btn btn-zoom" to={`/visit/${selected.id}`} onClick={close}>
              {JoinIcon} Start visit
            </Link>
          </div>
          <div className="appt-detail-foot appt-detail-foot--flush">
            <button
              className="btn-discontinue"
              onClick={() => {
                setActionError(null);
                setView("cancel");
              }}
            >
              {CloseXIcon} Cancel appointment
            </button>
            <button
              className="btn btn-primary"
              onClick={() => {
                setActionError(null);
                setRsOffset(rsOffsetForDate(selected.scheduled_at));
                setRsTime(null);
                setView("reschedule");
              }}
            >
              {RescheduleIcon} Reschedule
            </button>
          </div>
        </>
      )}

      {view === "reschedule" && (
        <>
          <div className="reschedule-current">
            <div className="icon">{CalendarIcon}</div>
            <div>
              <div className="label">Currently scheduled</div>
              <div className="value">
                {DAY_NAMES_FULL[d0.getDay()]}, {MONTH_SHORT[d0.getMonth()]} {d0.getDate()} at{" "}
                {formatTime(selected.scheduled_at)} · {selected.duration_minutes} min
              </div>
            </div>
          </div>

          <div className="reschedule-section">
            <h4>
              New date <span className="hint">scroll for more →</span>
            </h4>
            <div className="date-pills">
              {rsOptions.map((o) => {
                const d = rsDateForOffset(o);
                const isSel = effectiveRsOffset === o;
                const isTodayPill = sameDate(d, rsStartOfDay(new Date()));
                return (
                  <button
                    key={o}
                    type="button"
                    className={`date-pill ${isSel ? "selected" : ""}`}
                    onClick={() => {
                      setRsOffset(o);
                      setRsTime(null);
                    }}
                  >
                    <div className="day-name">{DAY_NAMES_SHORT[d.getDay()]}</div>
                    <div className="day-num">{d.getDate()}</div>
                    {isTodayPill ? (
                      <div className="today-tag">Today</div>
                    ) : (
                      <div className="today-tag" style={{ visibility: "hidden" }}>·</div>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="reschedule-section">
            <h4>
              Available times{" "}
              <span className="hint">
                {MONTH_SHORT[rsDateForOffset(effectiveRsOffset).getMonth()]} {rsDateForOffset(effectiveRsOffset).getDate()}
              </span>
            </h4>
            <div className="time-slots">
              {slots.map((slot) => {
                const isSel = rsTime === slot.min;
                const cls = ["time-slot"];
                if (slot.status === "disabled") cls.push("disabled");
                if (isSel) cls.push("selected");
                if (slot.current && !isSel) cls.push("current");
                return (
                  <button
                    key={slot.min}
                    type="button"
                    className={cls.join(" ")}
                    disabled={slot.status === "disabled"}
                    onClick={slot.status === "available" ? () => setRsTime(slot.min) : undefined}
                  >
                    {clock(slot.min)}
                    {slot.label ? (
                      <span className="conflict">{slot.label}</span>
                    ) : slot.current ? (
                      <span className="conflict">Current</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="appt-detail-foot">
            <button
              className="btn btn-ghost"
              onClick={() => {
                setRsTime(null);
                setView("detail");
              }}
            >
              {BackIcon} Back
            </button>
            <div className="right">
              <button className="btn btn-ghost" onClick={close}>
                Cancel
              </button>
              <button className="btn btn-primary" disabled={!canConfirmRs || busy} onClick={confirmReschedule}>
                {busy ? "Rescheduling…" : "Confirm reschedule"}
              </button>
            </div>
          </div>
        </>
      )}

      {view === "cancel" && (
        <>
          <div style={{ textAlign: "center", padding: "8px 12px 4px" }}>
            <div className="dc-icon">{CloseXIcon}</div>
            <p style={{ color: "var(--muted)", maxWidth: 420, margin: "0 auto 18px", lineHeight: 1.55, fontSize: 14 }}>
              This visit will be removed from your schedule, and {nameOf(selected.patient_id)} will be notified.
            </p>
          </div>
          <div className="dc-input" style={{ maxWidth: 440, margin: "0 auto" }}>
            <label htmlFor="apptCancelReason">Reason for cancellation</label>
            <select id="apptCancelReason" value={cancelReason} onChange={(e) => setCancelReason(e.target.value)}>
              {CANCEL_REASONS.map((r) => (
                <option key={r}>{r}</option>
              ))}
            </select>
          </div>
          <div className="dc-input" style={{ maxWidth: 440, margin: "14px auto 0" }}>
            <label htmlFor="apptCancelNote">Note (optional)</label>
            <textarea
              id="apptCancelNote"
              value={cancelNote}
              onChange={(e) => setCancelNote(e.target.value)}
              placeholder="Additional context…"
              style={{ minHeight: 60 }}
            />
          </div>
          <div className="appt-detail-foot">
            <button className="btn btn-ghost" onClick={() => setView("detail")}>
              Keep appointment
            </button>
            <button className="btn-discontinue" disabled={busy} onClick={confirmCancel}>
              {busy ? "Cancelling…" : "Cancel appointment"}
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

// ===========================================================================
// Weekly-hours editor (unchanged behavior) and Time-off / blocks (unchanged).
// ===========================================================================
function WeeklyEditor({
  weekly,
  onClose,
  onSaved,
}: {
  weekly: WeeklySlot[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const api = useApi();
  const [rows, setRows] = useState<WeekRow[]>(() => buildRows(weekly));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRows(buildRows(weekly));
  }, [weekly]);

  function setRow(wd: number, patch: Partial<WeekRow>) {
    setRows((rs) => rs.map((r) => (r.weekday === wd ? { ...r, ...patch } : r)));
  }

  async function save() {
    setBusy(true);
    try {
      await api.setWeekly(
        rows.filter((r) => r.on).map((r) => ({ weekday: r.weekday, starts_at: r.starts_at, ends_at: r.ends_at })),
      );
      onSaved();
      onClose();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} wide labelledBy="weeklyTitle">
        <div className="dp-card__title" id="weeklyTitle" style={{ marginBottom: 4 }}>
          Weekly hours
        </div>
        <p className="dp-card__sub">Your standard recurring hours for the week.</p>
        <div className="dp-week">
          {rows.map((r) => (
            <div key={r.weekday} className={`dp-wrow ${r.on ? "" : "is-off"}`}>
              <div className="dp-wrow__day">
                <label className="dp-switch">
                  <input
                    type="checkbox"
                    checked={r.on}
                    onChange={(e) => setRow(r.weekday, { on: e.target.checked })}
                    aria-label={weekdayLabel(r.weekday)}
                  />
                  <span className="dp-switch__track" />
                </label>
                <span className="dp-wrow__name">{weekdayLabel(r.weekday)}</span>
              </div>
              <div className="dp-times">
                <input
                  type="time"
                  className="dp-time"
                  value={r.starts_at}
                  disabled={!r.on}
                  onChange={(e) => setRow(r.weekday, { starts_at: e.target.value })}
                />
                <span>to</span>
                <input
                  type="time"
                  className="dp-time"
                  value={r.ends_at}
                  disabled={!r.on}
                  onChange={(e) => setRow(r.weekday, { ends_at: e.target.value })}
                />
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 18, display: "flex", gap: 10 }}>
          <button className="dp-btn dp-btn--primary" disabled={busy} onClick={save}>
            {busy ? "Saving…" : "Set weekly hours"}
          </button>
          <button className="dp-btn dp-btn--ghost" disabled={busy} onClick={onClose}>
            Cancel
          </button>
        </div>
    </Modal>
  );
}

function TimeOff({
  exceptions,
  onClose,
  onChanged,
}: {
  exceptions: Exception[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const api = useApi();
  const [starts, setStarts] = useState("");
  const [ends, setEnds] = useState("");
  const [kind, setKind] = useState<"pto" | "block">("pto");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  async function add() {
    if (!starts || !ends) return;
    setBusy(true);
    try {
      await api.addException({
        starts_at: new Date(starts).toISOString(),
        ends_at: new Date(ends).toISOString(),
        kind,
        reason: reason.trim() || undefined,
      });
      setStarts("");
      setEnds("");
      setReason("");
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal open onClose={onClose} wide labelledBy="timeoffTitle">
        <div className="dp-card__title" id="timeoffTitle" style={{ marginBottom: 4 }}>
          Time off &amp; blocks
        </div>
        <p className="dp-card__sub">Vacation or blocked time auto-removes those slots from your calendar.</p>
        {exceptions.length ? (
          <div>
            {exceptions.map((e) => (
              <div key={e.id} className="dp-exc">
                <span className="dp-exc__kind">{e.kind}</span>
                <span className="dp-exc__when">
                  {new Date(e.starts_at).toLocaleString()} &rarr; {new Date(e.ends_at).toLocaleString()}
                  {e.reason ? <span className="dp-exc__reason"> · {e.reason}</span> : null}
                </span>
                <button
                  className="dp-exc__rm"
                  onClick={async () => {
                    await api.deleteException(e.id);
                    onChanged();
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="muted">No upcoming time off.</p>
        )}

        <div className="dp-form-grid">
          <div className="dp-field">
            <label htmlFor="from">From</label>
            <input id="from" type="datetime-local" className="dp-input" value={starts} onChange={(e) => setStarts(e.target.value)} />
          </div>
          <div className="dp-field">
            <label htmlFor="to">To</label>
            <input id="to" type="datetime-local" className="dp-input" value={ends} onChange={(e) => setEnds(e.target.value)} />
          </div>
          <div className="dp-field">
            <label htmlFor="kind">Type</label>
            <select id="kind" className="dp-input" value={kind} onChange={(e) => setKind(e.target.value as "pto" | "block")}>
              <option value="pto">Time off (PTO)</option>
              <option value="block">Block</option>
            </select>
          </div>
          <div className="dp-field">
            <label htmlFor="reason">Reason</label>
            <input id="reason" className="dp-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional" />
          </div>
        </div>
        <div style={{ marginTop: 16, display: "flex", gap: 10 }}>
          <button className="dp-btn dp-btn--primary" disabled={busy || !starts || !ends} onClick={add}>
            {busy ? "Saving…" : "Request time off"}
          </button>
          <button className="dp-btn dp-btn--ghost" disabled={busy} onClick={onClose}>
            Close
          </button>
        </div>
    </Modal>
  );
}
